import React, { useReducer, useContext } from 'react';
import axios from 'axios';
import CrmContext from './CrmContext';
import CrmReducer from './CrmReducer';
import MainContext from '../main/MainContext';
import AuthContext from '../auth/AuthContext';
import {
  SET_PROGRESS,
  SET_IDS,
  SET_CRM_LIST,
  SET_ACTIVE_CRM_KEY,
  SET_ACTIVE_CRM_HEADER,
  SET_ACTIVE_CRM_DATA,
  SET_CRM_STATE_TO_INITIAL,
  SET_CRM_LOOKALIKE,
  SET_CRM_AUDIENCE_SIZE,
  SET_AUDIENCE_SIZE,
  SET_CRM_DATASET,
  SET_PERSISTENT_DATASET,
  RESET_ERROR,
  RESET_CRM_ID,
  SET_CRM_HEADERS,
  SET_NICKNAME,
  SET_NON_PERSISTENT_DATASET,
  SET_CRM_TEMPLATE_USE,
} from '../types';
import { CRMTYPE } from '../../components/Utilities/NamingConversions';
import AudienceContext from '@context/audience/AudienceContext';


export default function CrmState(props) {
  const authContext = useContext(AuthContext);
  const mainContext = useContext(MainContext);
  const { deleteAudience } = useContext(AudienceContext);
  const { loadingDecrement, loadingIncrement, handleError } = mainContext;

  const initialState = {
    ids: null,
    defineCus: {},
    crmList: [],
    crmDataStoreKey: null,
    headers: {
      FSA: true,
      'Postal Code': false,
      Age: true,
      Gender: true,
    },
    crmDataHeaders: [], // list of headers that used for particular crm submission. matches the order of columns in hottable.
    data: [],
    lookalike: false,
    audienceSize: null,
    dataset: null,
    error: false,
    errorText: '',
    crmTemplate: false,
  };
  const [state, dispatch] = useReducer(CrmReducer, initialState);

  const getCRMList = async () => {
    try {
      loadingIncrement(
        CRMTYPE,
        'Please wait while we retrieve your saved data...'
      );
      const token = await authContext.getIdToken();

      const res = await axios.post(
        '/get_crm_list',
        {
          countryCode: authContext.userCountryCode,
        },
        {
          headers: {
            Authorization: token,
          },
        }
      );
      const sortedRes = res.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      dispatch({
        type: SET_CRM_LIST,
        payload: sortedRes,
      });
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(CRMTYPE);
    }
  };

  const SubmitCRM = async (
    saveData = false,
    title = '',
    refreshCRMList = true
  ) => {
    try {
      loadingIncrement(CRMTYPE, 'Please wait while we process you data...');
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/process_crm_csv',
        {
          data: state.data,
          headers: state.crmDataHeaders,
          title,
          lookalike: state.lookalike,
          save_data: saveData,
          countryCode: authContext.userCountryCode,
          timestamp: state.ids,
          dataset: state.dataset,
          audienceSize: state.audienceSize,
          template: state.crmTemplate,
        },
        {
          headers: { Authorization: token },
        }
      );
      if (!saveData) {
        dispatch({
          type: SET_ACTIVE_CRM_KEY,
          payload: res.data.key,
        });
        dispatch({
          type: SET_IDS,
          payload: res.data.id,
        });
        dispatch({
          type: SET_CRM_AUDIENCE_SIZE,
          payload: res.data.audience_size,
        });
        dispatch({
          type: SET_CRM_DATASET,
          payload: res.data.dataset,
        });
        mainContext.dispatch({
          type: SET_PROGRESS,
        });
        mainContext.dispatch({
          type: SET_IDS,
          payload: { ids: res.data.id },
        });
        mainContext.dispatch({
          type: SET_AUDIENCE_SIZE,
          payload: res.data.audience_size,
        });
        mainContext.dispatch({
          type: 'SET_CRM',
          payload: true,
        });
        mainContext.dispatch({
          type: SET_NON_PERSISTENT_DATASET,
          payload: res.data.dataset,
        });
        mainContext.dispatch({
          type: SET_NICKNAME,
          payload: '',
        });
      } else {
        mainContext.dispatch({
          type: SET_NICKNAME,
          payload: title,
        });
        mainContext.dispatch({
          type: SET_PERSISTENT_DATASET,
          payload: state.dataset,
        });
        return res.data.crm_entity_key;
      }
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(CRMTYPE);
      if (refreshCRMList) getCRMList();
    }
  };

  const deleteCRM = async ({ crmId, type = CRMTYPE } = {}) => {
    try {
      loadingIncrement(type);
      const token = await authContext.getIdToken();
      const audience_id = state.crmList.filter(
        (crmAudience) => crmAudience.id === crmId
      )[0].saved_audience_key;
      await axios.post(
        '/delete_crm',
        {
          key: crmId,
          countryCode: authContext.userCountryCode,
        },
        {
          headers: { Authorization: token },
        }
      );
      await deleteAudience(audience_id);
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
      getCRMList();
    }
  };

  const editCRM = async ({ crmId, type = CRMTYPE } = {}) => {
    try {
      loadingIncrement(type, 'Please wait while we retrieve your data...');
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/edit_crm',
        {
          key: crmId,
          countryCode: authContext.userCountryCode,
        },
        {
          headers: { Authorization: token },
        }
      );
      const crmData = '[' + res.data.data + ']';
      const csvHeader = Object.keys(res.data.columns);
      csvHeader.forEach(function (part, index, theArray) {
        theArray[index] = part.toUpperCase();
      });
      const updatedHeader = { ...state.headers };
      Object.keys(updatedHeader).forEach(
        (key) => (updatedHeader[key] = csvHeader.includes(key.toUpperCase()))
      );
      dispatch({ type: SET_ACTIVE_CRM_HEADER, payload: updatedHeader });
      dispatch({ type: SET_ACTIVE_CRM_DATA, payload: JSON.parse(crmData) });
      dispatch({ type: SET_CRM_LOOKALIKE, payload: res.data.lookalike });
      dispatch({ type: SET_IDS, payload: res.data.id });
      dispatch({ type: SET_CRM_TEMPLATE_USE, payload: res.data.template });
      dispatch({
        type: SET_CRM_DATASET,
        payload: res.data.dataset,
      });
      dispatch({
        type: SET_CRM_AUDIENCE_SIZE,
        payload: res.data.audience_size,
      });
      mainContext.dispatch({
        type: SET_AUDIENCE_SIZE,
        payload: res.data.audience_size,
      });
      mainContext.dispatch({
        type: SET_PROGRESS,
      });
      mainContext.dispatch({
        type: SET_IDS,
        payload: { ids: res.data.id },
      });
      mainContext.dispatch({
        type: SET_PERSISTENT_DATASET,
        payload: res.data.dataset,
      });
      mainContext.dispatch({
        type: SET_NICKNAME,
        payload: res.data.name,
      });
      mainContext.dispatch({
        type: 'SET_CRM',
        payload: true,
      });
    } catch (err) {
      handleError(err);
    } finally {
      loadingDecrement(type);
    }
  };

  const setCRMData = async (data) => {
    dispatch({ type: SET_ACTIVE_CRM_DATA, payload: data });
  };

  const resetError = () => {
    dispatch({ type: RESET_ERROR });
  };

  const setCRMDataHeaders = (headers) => {
    dispatch({ type: SET_ACTIVE_CRM_HEADER, payload: headers });
  };

  const resetCRMState = () => {
    dispatch({ type: SET_CRM_STATE_TO_INITIAL, payload: initialState });
  };

  const resetCRMID = () => {
    dispatch({ type: RESET_CRM_ID });
  };

  const setCRMColumnHeaders = (data) => {
    dispatch({ type: SET_CRM_HEADERS, payload: data });
  };

  const setCRMLookalike = (lookalike) => {
    dispatch({ type: SET_CRM_LOOKALIKE, payload: lookalike });
  };

  const setUseDataTemplate = (value = false) => {
    dispatch({ type: SET_CRM_TEMPLATE_USE, payload: value });
  };

  return (
    <CrmContext.Provider
      value={{
        getCRMList,
        setCRMData,
        SubmitCRM,
        deleteCRM,
        editCRM,
        setCRMDataHeaders,
        resetCRMState,
        setCRMLookalike,
        resetError,
        resetCRMID,
        setCRMColumnHeaders,
        setUseDataTemplate,
        crmDataStoreKey: state.crmDataStoreKey,
        crmList: state.crmList,
        crmId: state.ids,
        crmData: state.data,
        crmHeaders: state.headers,
        crmLookalike: state.lookalike,
        crmError: state.error,
        crmErrorText: state.errorText,
        crmColumns: state.crmDataHeaders,
        crmTemplate: state.crmTemplate,
      }}
    >
      {props.children}
    </CrmContext.Provider>
  );
}
